/* eslint-disable react/prop-types */
import React from 'react'
import algoliasearch from 'algoliasearch'
import { getAlgoliaResults } from '@algolia/autocomplete-js'
import { useTranslation } from 'react-i18next'
import usePersonalities from '../../hooks/usePersonalities'
import useUser from '../../hooks/useUser'
import { useApp } from '../../hooks/useAppContext'
import {
  addRecentCustomer,
  getRecentCustomers,
} from '../../helpers/recentCustomers'
import Autocomplete from './autocomplete'

function uniqBy(predicate) {
  return function runUniqBy(...rawSources) {
    const sources = rawSources.flat().filter(Boolean)
    const seen = new Set()

    return sources.map((source) => {
      const items = source.getItems().filter((item) => {
        const appliedItem = predicate({ source, item })
        const hasSeen = seen.has(appliedItem)

        seen.add(appliedItem)

        return !hasSeen
      })

      return {
        ...source,
        getItems() {
          return items
        },
      }
    })
  }
}

const matchesQuery = (query) => {
  const needle = query.toLowerCase().trim()

  return ({ name }) => name.toLowerCase().includes(needle)
}

// autocomplete-js wraps this in `.aa-SourceHeader`; this is the classic
// theme's inner markup.
const sourceHeader = (title) =>
  function SourceHeader() {
    return (
      <>
        <span className="aa-SourceHeaderTitle">{title}</span>
        <div className="aa-SourceHeaderLine" />
      </>
    )
  }

export default function Search({ customers, deletedCustomers }) {
  const { router } = useApp()
  const { getById } = usePersonalities()
  const { useGetMe, useGetCurrentOrganizationId } = useUser()
  const { user } = useGetMe()
  const { currentOrganizationId } = useGetCurrentOrganizationId({ user })
  const { t } = useTranslation('translation', {
    useSuspense: false,
    bindI18n: false,
  })

  const searchClient = algoliasearch(
    process.env.NEXT_PUBLIC_ALGOLIA_APPLICATION_ID,
    user?.search_api_key,
  )

  const removeDuplicates = uniqBy(({ item }) => item.hashid || item.id)

  // Algolia only navigates on its own for real `<a href>` items; ours use
  // shallow client-side routing, so navigate from `onSelect`. It fires for a
  // click/tap anywhere on the row (not just the text) and for Enter.
  const openCustomer = ({ item, setIsOpen, setQuery }) => {
    addRecentCustomer(user?.id, currentOrganizationId, item)
    setIsOpen(false)
    setQuery('')
    router.push(item.query, item.slug, { shallow: true })
  }

  const searchResultTemplate = (query) => {
    return {
      item({ item }) {
        const highlights = (name) => {
          const index = name.toLowerCase().indexOf(query.toLowerCase().trim())
          const endIndex = index + query.toLowerCase().trim().length

          return (
            <>
              <span>{name.substring(0, index)}</span>
              <span className="bg-indigo-200">
                {name.substring(index, endIndex)}
              </span>
              <span>{name.substring(endIndex)}</span>
            </>
          )
        }

        return (
          <div className="aa-ItemWrapper">
            <div className="aa-ItemContent">
              <div className="text-gray-800 aa-ItemTitle">
                <>
                  <span>{getById(item.personality).icon}</span>{' '}
                  {highlights(item.name)}
                </>
              </div>
            </div>
          </div>
        )
      },
    }
  }

  return (
    <Autocomplete
      openOnFocus={true}
      navigator={{
        // Enter triggers onSelect (which navigates) *and* navigate(); keep
        // this a no-op so we don't push the route twice.
        navigate() {},
      }}
      getSources={({ query }) => [
        {
          sourceId: 'recentCustomers',
          getItems() {
            return getRecentCustomers(user?.id, currentOrganizationId)
              .filter((c) => (deletedCustomers || []).indexOf(c.id) === -1)
              .filter(matchesQuery(query))
          },
          getItemUrl({ item }) {
            return item.slug
          },
          onSelect: openCustomer,
          templates: {
            ...searchResultTemplate(query),
            header: sourceHeader(t('search.recentlyViewed')),
          },
        },
        {
          sourceId: 'remoteCustomers',
          getItems() {
            return getAlgoliaResults({
              searchClient,
              queries: [
                {
                  indexName: `Customer_${process.env.NODE_ENV}`,
                  query,
                  params: {
                    hitsPerPage: query === '' ? 10 : 1000,
                    filters: `visible_by:${currentOrganizationId}`,
                  },
                },
              ],
              transformResponse({ hits }) {
                return hits
                  .flat()
                  .filter(
                    (c) => (deletedCustomers || []).indexOf(c.hashid) === -1,
                  )
                  .map((c) => {
                    return {
                      id: c.hashid,
                      personality: c.personality,
                      name: c.name,
                      slug: `/c/${c.hashid}`,
                      query: `?c=${c.hashid}`,
                    }
                  })
              },
            })
          },
          getItemUrl({ item }) {
            return item.slug
          },
          onSelect: openCustomer,
          templates: {
            ...searchResultTemplate(query),
            header: sourceHeader(t('search.customers')),
          },
        },
        {
          sourceId: 'localCustomers',
          getItems() {
            return (customers || [])
              .map((c) => {
                return {
                  id: c.id,
                  personality: c.personality,
                  name: c.name,
                  slug: `/c/${c.id}`,
                  query: `?c=${c.id}`,
                }
              })
              .filter(matchesQuery(query))
          },
          getItemUrl({ item }) {
            return item.slug
          },
          onSelect: openCustomer,
          templates: searchResultTemplate(query),
        },
      ]}
      reshape={({ sourcesBySourceId }) => {
        const { recentCustomers, remoteCustomers, localCustomers, ...rest } =
          sourcesBySourceId
        // Recents win the dedupe so a customer never shows up twice.
        const [recent, remote, local] = removeDuplicates(
          recentCustomers,
          remoteCustomers,
          localCustomers,
        )

        return [
          recent,
          // One "Customers" section: Algolia hits, then local-only ones.
          {
            ...remote,
            getItems() {
              return [...remote.getItems(), ...local.getItems()]
            },
          },
          Object.values(rest),
        ]
      }}
    />
  )
}
